import { FC, useEffect, useMemo, useReducer, useState } from 'react';
import {
    LoginOutlined,
    LoadingOutlined,
    CheckCircleOutlined,
    MinusCircleOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import SearchCustomerButton from '../../../../common/search-customer/SearchCustomerButton';
import { InfoTitle } from '../../../../common/DeclarationInformation.styles';
import { Tooltip } from 'antd';
import ConditionalWrapper from '../../../../../../components/ConditionalWrapper';
import { Events } from 'utils/events.enum';

interface InvoiceStatuses {
    loading: boolean;
    failed: boolean;
    invoiceSavedDisable: boolean;
}

const initialInvoiceStatuses: InvoiceStatuses = {
    loading: true,
    failed: false,
    invoiceSavedDisable: false,
};

const invoiceStatusesReducer = (state: InvoiceStatuses, newState: Partial<InvoiceStatuses>): InvoiceStatuses =>
    ({
        ...state,
        ...newState,
    } as InvoiceStatuses);

const InvoiceFormHeader: FC<{ disabled: boolean }> = ({ disabled: initiallyDisabled }) => {
    const [handleOpenInvoiceModalCallback, setHandleOpenInvoiceModalCallback] = useState<() => void>(() => {});
    const [invoiceStatuses, setInvoiceStatuses] = useReducer(invoiceStatusesReducer, initialInvoiceStatuses);

    useEffect(() => {
        const handleInvoiceModalSubscriberId = window.EventBus.subscribe(
            Events.HANDLE_INVOICE_MODAL,
            (callback: () => void) => {
                setHandleOpenInvoiceModalCallback(() => callback);
                setInvoiceStatuses({ loading: false });
            }
        );

        const invoiceModalSavedSubscriberId = window.EventBus.subscribe(Events.INVOICE_MODAL_SAVED, () =>
            setInvoiceStatuses({ invoiceSavedDisable: false })
        );

        const invoiceFailedInitiallySubscriberId = window.EventBus.subscribe(
            Events.INVOICE_FAILED_INITIALLY,
            () => () => setInvoiceStatuses({ failed: true, loading: false })
        );

        return () => {
            window.EventBus.unsubscribe(handleInvoiceModalSubscriberId);
            window.EventBus.unsubscribe(invoiceModalSavedSubscriberId);
            window.EventBus.unsubscribe(invoiceFailedInitiallySubscriberId);
        };
    }, []);

    const isDisabled = initiallyDisabled || invoiceStatuses.loading || invoiceStatuses.invoiceSavedDisable;

    const invoiceFormHeaderIcon = useMemo(() => {
        if (isDisabled) {
            return <MinusCircleOutlined style={{ color: 'grey', marginRight: 7 }} />;
        }

        if (invoiceStatuses.failed) {
            return <CloseCircleOutlined style={{ color: 'red', marginRight: 7 }} />;
        }

        return <CheckCircleOutlined style={{ color: 'green', marginRight: 7 }} />;
    }, [isDisabled, invoiceStatuses.failed]);

    return (
        <ConditionalWrapper
            condition={initiallyDisabled}
            wrapper={(children) => (
                <Tooltip placement="left" title="The feature becomes available when an invoice is uploaded.">
                    {children}
                </Tooltip>
            )}
        >
            <div style={{ display: 'flex', gap: 30, alignItems: 'center' }}>
                <InfoTitle
                    style={{
                        fontSize: 'large',
                        color: isDisabled ? 'grey' : '',
                    }}
                >
                    {invoiceFormHeaderIcon}
                    Invoice Extraction
                </InfoTitle>
                <SearchCustomerButton
                    condensed
                    title="Open Extraction"
                    icon={<LoginOutlined />}
                    disabled={isDisabled}
                    onClick={handleOpenInvoiceModalCallback}
                />
            </div>
        </ConditionalWrapper>
    );
};

export default InvoiceFormHeader;
